import { track } from '@/common/track';
import { Button } from '@/components/Button';
import { CardSkeleton } from '@/components/CardSkeleton';
import { AddTrainingDialog } from '@/components/dialogs/AddTrainingDialog';
import { CourseFinderDialog } from '@/components/dialogs/CourseFinderDialog';
import {
  CourseStudentsDialog,
  CourseStudentsDialogType,
} from '@/components/dialogs/CourseStudentsDialog';
import { ManageTrainingCard } from '@/components/ManageTrainingCard';
import {
  ManageTrainingTable,
  ManageTrainingTableRow,
} from '@/components/table/ManageTrainingTable';
import { TablePlaceholder } from '@/components/TablePlaceholder';
import { TextField } from '@/components/TextField';
import { ToggleButton } from '@/components/ToggleButton';
import {
  TrainingCourseFilterDrawer,
  TrainingCourseFilters,
} from '@/components/TrainingCourseFilterDrawer';
import {
  CEVENTAS_PROVIDER_ID,
  COURSE_RECURRENCE,
  CREATE_TRAINING_MODE,
  IntegrationSource,
  PAGE_SIZE_OPTIONS,
  TRAINING_ATTRIBUTE_DIALOG_TYPE,
} from '@/constants';
import { queryKeys } from '@/hooks/queryKeys';
import useDebounce from '@/hooks/useDebounce';
import { useDeleteCourse } from '@/hooks/useDeleteCourse';
import { useGetTrainingCourses } from '@/hooks/useGetTrainingCourses';
import { useUpdateCourseRecurrence } from '@/hooks/useUpdateCourseRecurrence';
import { useAppData } from '@/providers/AppDataProvider';
import { useLocale } from '@/providers/LocaleProvider';
import { useManageLearning } from '@/providers/ManageLearningProvider';
import AddIcon from '@/resources/images/icons-add.svg';
import AISparkeIcon from '@/resources/images/icons-ai-sparkle.svg';
import FilterIcon from '@/resources/images/icons-filter.svg';
import RemoveIcon from '@/resources/images/icons-remove.svg';
import SearchIcon from '@/resources/images/icons-search.svg';
import { useNavigation } from '@/router';
import { KLAYO_COLORS } from '@/themes';
import { BaseSelectMenuOption, BaseSelectorOption } from '@/types/components/selector.types';
import { ITrainingCourse, ITrainingCourseProvider } from '@/types/views/trainingCourse.types';
import { TrainingAttributesDialog } from '@/views/settings/TrainingAttributesDialog';
import GridViewIcon from '@mui/icons-material/GridView';
import ReorderIcon from '@mui/icons-material/Reorder';
import {
  Box,
  Chip,
  Grid,
  ListItemText,
  Menu,
  MenuItem,
  Pagination,
  Select,
  SelectChangeEvent,
  Stack,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { GridPaginationModel, GridSortModel } from '@mui/x-data-grid-pro';
import { useQueryClient } from '@tanstack/react-query';
import { ConfirmationDialog, ManageTrainingEvent } from 'klayowebshared';
import { SyntheticEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';

const mapCourseToItem = (course: ITrainingCourse): ManageTrainingTableRow => ({
  id: course.id,
  courseId: course.sku,
  courseName: course.title,
  thumbnailUrl: course.imageUrl ?? undefined,
  numberOfStars: Math.round(course.averageRating),
  enrolledCount: course.enrolled,
  completedCount: course.completed,
  inProgressCount: course.inProgress,
  overdueCount: course.overdue,
  competencies: course.associatedAttributeDefinitions,
  competencyCount: course.associatedAttributeDefinitions.length,
  competenciesLocked: course.competenciesLocked,
  integrationId: course.integrationId,
  duration: course.duration ?? '',
  recurrence: course.recurrence,
  integrationSource: course.integrationSource,
  facultiesAsString: course.facultiesAsString,
  integrationName: course.integrationName,
  organizationIntegrationAbbreviation: course.integrationAbbreviation || course.integrationName,
  courseType: course.courseType,
  isLearningPath: course.isLearningPath,
});

const GridList = styled('div')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr',
  gap: '24px',
  [theme.breakpoints.up('sm')]: {
    gridTemplateColumns: 'repeat(2, 1fr)',
  },
  [theme.breakpoints.up('md')]: {
    gridTemplateColumns: 'repeat(3, 1fr)',
  },
  [theme.breakpoints.up('xl')]: {
    gridTemplateColumns: 'repeat(4, 1fr)',
  },
}));

const mapProviders = (providers: ITrainingCourseProvider[]): [string, BaseSelectMenuOption][] =>
  providers.map((p) => [p.name, { label: p.name, value: p.integrationId ?? '' }]);

export const ManageTrainingCourses = () => {
  const { translate } = useLocale();
  const { history } = useNavigation();
  const { organization } = useAppData();
  const { manageCoursesFilterState, dispatchManageCoursesFilter } = useManageLearning();
  const { mutate: updateRecurrence } = useUpdateCourseRecurrence();
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
  const navigate = useNavigate();

  const { search, paginationModel, sortBy, orderBy, filters, viewMode } = manageCoursesFilterState;
  const searchDebounce = useDebounce(search.trim(), 500);

  useEffect(() => {
    track(ManageTrainingEvent.CoursesViewed);
  }, []);

  useEffect(() => {
    if (searchDebounce) track(ManageTrainingEvent.CoursesSearch, { text: searchDebounce });
  }, [searchDebounce]);

  const sortModel: GridSortModel = useMemo(
    () => [{ field: sortBy, sort: orderBy }],
    [sortBy, orderBy]
  );

  const { data, isPending: isLoading } = useGetTrainingCourses({
    searchText: searchDebounce,
    paginationModel,
    sortModel,
    providerIds: filters.providers.map((p) => p.value as string).filter(Boolean),
    regionNames: filters.regions.map((r) => r.id as string),
    facultyIds: filters.faculties.map((f) => f.id as string),
    languageCodes: filters.languages.map((l) => l.id as string),
    courseTypes: filters.courseTypes.map((c) => c.id as string),
    priceMin: filters.priceMin !== null ? filters.priceMin * 100 : undefined,
    priceMax: filters.priceMax !== null ? filters.priceMax * 100 : undefined,
    hasEnrolments: filters.hasEnrolments,
  });

  const { courseFiltersData: filtersData } = useManageLearning();
  const providerOptions = useMemo(
    () => mapProviders(filtersData?.filters?.providers ?? []),
    [filtersData]
  );
  const manualProviderOptions = useMemo(
    () =>
      (
        filtersData?.filters?.providers?.filter(
          (p) => p.integrationSource === IntegrationSource.Manual
        ) ?? []
      ).map((p) => ({ id: p.integrationId, name: p.name })),
    [filtersData]
  );

  const isEnabledTraining = useMemo(
    () => !!organization?.enabledFeatures?.Training,
    [organization]
  );

  const [showFilters, setShowFilters] = useState(false);
  const [showAddTraining, setShowAddTraining] = useState(false);
  const [studentsDialogOpen, setStudentsDialogOpen] = useState(false);
  const [studentsDialogType, setStudentsDialogType] =
    useState<CourseStudentsDialogType>('enrolled');
  const [selectedCourse, setSelectedCourse] = useState<ManageTrainingTableRow | null>(null);
  const [trainingAttributesDetail, setTrainingAttributesDetail] = useState<any>(null);
  const [trainingDialogType, setTrainingDialogType] = useState<number>();
  const [actionTarget, setActionTarget] = useState<HTMLElement | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showCourseFinder, setShowCourseFinder] = useState(false);
  const [showPremiumDialog, setShowPremiumDialog] = useState(false);

  const queryClient = useQueryClient();

  const { mutate: deleteCourse, isPending: isDeleting } = useDeleteCourse(
    selectedCourse?.id || '',
    () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.trainingCourses.list() });
      setShowDeleteConfirm(false);
      setSelectedCourse(null);
    }
  );

  const handleSortModelChange = useCallback(
    (model: GridSortModel) => {
      const sort = model[0];
      track(ManageTrainingEvent.CoursesOrder, {
        column: sort?.field || 'title',
        ascending: (sort?.sort || 'asc') === 'asc',
      });
      dispatchManageCoursesFilter({
        type: 'SET_SORT',
        payload: {
          sortBy: sort?.field || 'title',
          orderBy: (sort?.sort || 'asc') as 'asc' | 'desc',
        },
      });
    },
    [dispatchManageCoursesFilter]
  );

  const handlePaginationModelChange = useCallback(
    (model: GridPaginationModel) => {
      if (isLoading) return;
      dispatchManageCoursesFilter({ type: 'SET_PAGINATION', payload: model });
    },
    [dispatchManageCoursesFilter, isLoading]
  );

  const handleCountBadgeClick = (
    _e: React.MouseEvent,
    row: ManageTrainingTableRow,
    type: CourseStudentsDialogType
  ) => {
    track(ManageTrainingEvent.ViewedStudents);
    setSelectedCourse(row);
    setStudentsDialogType(type);
    setStudentsDialogOpen(true);
  };

  const handleCompetencyClick = (row: ManageTrainingTableRow) => {
    setTrainingAttributesDetail({
      name: row.courseName,
      associatedAttribute: row.competencies,
    });
    setTrainingDialogType(TRAINING_ATTRIBUTE_DIALOG_TYPE.DETAIL);
  };

  const checkHasExpiredData = useCallback(
    (associatedAttribute: Array<{ hasExpiryDate?: boolean }>, recurrence: number): boolean => {
      return associatedAttribute.every((attribute) => {
        if (recurrence !== COURSE_RECURRENCE.NONE.value) return attribute?.hasExpiryDate;
        return !attribute?.hasExpiryDate;
      });
    },
    []
  );

  const handleShowAttributesDialog = useCallback(
    (detail: { name: string; associatedAttribute: any[]; newRecurrence: number }, type: number) => {
      setTrainingAttributesDetail(detail);
      setTrainingDialogType(type);
    },
    []
  );

  const handleBeforeSave = useCallback(
    (detail: { name: string; associatedAttribute: any[]; recurrence: number }): boolean => {
      if (!checkHasExpiredData(detail.associatedAttribute, detail.recurrence)) {
        handleShowAttributesDialog(
          {
            name: detail.name,
            associatedAttribute: detail.associatedAttribute,
            newRecurrence: detail.recurrence,
          },
          TRAINING_ATTRIBUTE_DIALOG_TYPE.WARNING
        );
        return false;
      }
      return true;
    },
    [checkHasExpiredData, handleShowAttributesDialog]
  );

  const handleRecurrenceChange = useCallback(
    (
      row: ManageTrainingTableRow,
      recurrence: (typeof COURSE_RECURRENCE)[keyof typeof COURSE_RECURRENCE]
    ) => {
      const canProceed = handleBeforeSave({
        name: row.courseName ?? '',
        associatedAttribute: row.competencies ?? [],
        recurrence: recurrence.value,
      });

      if (canProceed) {
        updateRecurrence({ id: row.id, recurrence: recurrence.value });
        track(ManageTrainingEvent.CoursesRecurrence, {
          courseName: row.courseName,
          recurrence: translate(recurrence.label),
        });
      }
    },
    [updateRecurrence, handleBeforeSave, translate]
  );

  const totalCount = data?.courses?.totalCount ?? 0;
  const items = useMemo(() => data?.courses?.data?.map(mapCourseToItem) ?? [], [data]);

  const rangeText = useMemo(() => {
    const { page, pageSize } = paginationModel;
    return `${page * pageSize + 1}-${Math.min((page + 1) * pageSize, totalCount)} of ${totalCount}`;
  }, [paginationModel, totalCount]);

  const onChangeViewMode = (_e: any, mode: 'grid' | 'list') => {
    if (mode === null) return;
    track(ManageTrainingEvent.CoursesToggleView, { viewTypeActivated: mode });
    dispatchManageCoursesFilter({ type: 'SET_VIEW_MODE', payload: mode });
  };

  const openCourseFinder = () => {
    track(ManageTrainingEvent.CoursesFinder);
    setShowCourseFinder(true);
  };

  const hasPriceFilter = filters.priceMin !== null || filters.priceMax !== null;

  const hasActiveFilters =
    filters.providers.length > 0 ||
    filters.regions.length > 0 ||
    filters.faculties.length > 0 ||
    filters.courseTypes.length > 0 ||
    filters.languages.length > 0 ||
    hasPriceFilter ||
    filters.hasEnrolments;

  const handleRemoveFilterItem = (
    key: 'regions' | 'faculties' | 'courseTypes' | 'languages',
    itemId: string | number | null | undefined
  ) => {
    dispatchManageCoursesFilter({
      type: 'SET_FILTERS',
      payload: { ...filters, [key]: filters[key].filter((item) => item.id !== itemId) },
    });
  };

  const handleRemoveFilter = (key: keyof TrainingCourseFilters | 'price') => {
    const updated = { ...filters };
    if (key === 'price') {
      updated.priceMin = null;
      updated.priceMax = null;
    } else if (key === 'hasEnrolments') {
      updated.hasEnrolments = false;
    } else if (Array.isArray(updated[key])) {
      (updated[key] as BaseSelectorOption[]) = [];
    }
    dispatchManageCoursesFilter({ type: 'SET_FILTERS', payload: updated });
  };

  const handleRemoveProvider = (value: unknown) => {
    dispatchManageCoursesFilter({
      type: 'SET_FILTERS',
      payload: { ...filters, providers: filters.providers.filter((p) => p.value !== value) },
    });
  };

  const handleClearAllFilters = () => {
    dispatchManageCoursesFilter({
      type: 'SET_FILTERS',
      payload: {
        providers: [],
        regions: [],
        faculties: [],
        courseTypes: [],
        languages: [],
        priceMin: null,
        priceMax: null,
        hasEnrolments: false,
      },
    });
  };

  const handleApplyFilters = (applied: TrainingCourseFilters) => {
    // Send full filter info as one key per filter (mirrors catalogue:filter_set) so analytics can
    // break down exactly which values were applied — same filters, just a different screen/persona.
    const names = (items: { label?: unknown; name?: unknown }[]) =>
      items.map((item) => String(item.label ?? item.name));
    track(ManageTrainingEvent.CoursesFiltered, {
      providers: names(applied.providers),
      regions: names(applied.regions),
      faculties: names(applied.faculties),
      languages: names(applied.languages),
      courseTypes: names(applied.courseTypes),
      price:
        applied.priceMin !== null || applied.priceMax !== null
          ? `$${applied.priceMin ?? 0}-$${applied.priceMax ?? 0}`
          : null,
      hasEnrolments: applied.hasEnrolments,
    });
    dispatchManageCoursesFilter({ type: 'SET_FILTERS', payload: applied });
  };

  const handleRowAction = (e: React.MouseEvent, row: ManageTrainingTableRow) => {
    setSelectedCourse(row);
    setActionTarget(e.target as HTMLElement);
  };

  const onCloseActionMenu = () => {
    setActionTarget(null);
    setSelectedCourse(null);
  };

  const onOpenEditTrainingDialog = () => {
    setActionTarget(null);
    setShowAddTraining(true);
  };

  const onDeleteTraining = () => {
    setActionTarget(null);
    setShowDeleteConfirm(true);
  };

  const onDeleteConfirmed = () => {
    deleteCourse();
  };

  const onCancelDelete = () => {
    setShowDeleteConfirm(false);
    setSelectedCourse(null);
  };

  const onCloseStudentsDialog = () => {
    setStudentsDialogOpen(false);
    setSelectedCourse(null);
  };

  const handleRowSelect = (e: SyntheticEvent, row: ManageTrainingTableRow) => {
    if (row.integrationSource !== null) return;

    track(ManageTrainingEvent.CoursesViewCourse, { courseName: row.courseName });
    navigate(`/settings/learning/${row.id}`);
  };

  return (
    <Stack gap={5}>
      <Box>
        <Grid container spacing={2} alignItems='center'>
          <Grid item xs={12} sm={6} md={6}>
            <Stack direction={'row'} alignItems={'center'} gap={1}>
              <TextField
                value={search}
                dense={true}
                placeholder={translate('common.search')}
                fullWidth={true}
                autoComplete={false}
                leadingIcon={<SearchIcon />}
                onChange={(e) =>
                  dispatchManageCoursesFilter({ type: 'SET_SEARCH', payload: e.target.value })
                }
              />
              <Button
                variant='outlined'
                label={translate('manage.filters')}
                size='md'
                startIcon={<FilterIcon style={{ width: 16, height: 16 }} />}
                onClick={() => {
                  track(ManageTrainingEvent.CoursesFilter);
                  setShowFilters(true);
                }}
                sx={{
                  flex: 'none',
                }}
              />
              <Button
                variant='outlined'
                label={translate('manage.courseFinder')}
                size='md'
                sx={{
                  flex: 'none',
                  display: { xs: 'none', md: 'flex' },
                }}
                startIcon={
                  <AISparkeIcon width={20} height={20} color={KLAYO_COLORS.NeonBlueNB500} />
                }
                onClick={openCourseFinder}
              />
            </Stack>
          </Grid>
          <Grid
            item
            xs={12}
            sm={6}
            md={6}
            sx={{
              display: 'flex',
              justifyContent: {
                xs: 'flex-start',
                md: 'flex-end',
              },
              gap: 1,
            }}
          >
            <Stack
              gap={1}
              direction={'row'}
              alignItems={'center'}
              flex={{
                xs: 1,
                md: 'none',
              }}
              maxWidth={{ xs: '100%', md: 'none' }}
            >
              <Button
                variant='outlined'
                label={translate('manage.courseFinder')}
                size='md'
                sx={{
                  flex: 1,
                  display: { xs: 'flex', md: 'none' },
                }}
                startIcon={
                  <AISparkeIcon width={20} height={20} color={KLAYO_COLORS.NeonBlueNB500} />
                }
                onClick={openCourseFinder}
              />
              {isEnabledTraining && (
                <Button
                  variant='filled'
                  label={translate('manage.addTraining')}
                  size='md'
                  startIcon={<AddIcon style={{ width: 16, height: 16 }} />}
                  onClick={() => setShowAddTraining(true)}
                  sx={{
                    flex: {
                      xs: 1,
                      md: 'none',
                    },
                  }}
                />
              )}
              <ToggleButton
                options={[
                  {
                    value: 'grid',
                    icon: <GridViewIcon />,
                    tooltip: translate('team.cardView'),
                  },
                  {
                    value: 'list',
                    icon: <ReorderIcon />,
                    tooltip: translate('team.listView'),
                  },
                ]}
                value={viewMode}
                onChange={onChangeViewMode}
                sx={{
                  display: { xs: 'none', md: 'flex' },
                }}
              />
            </Stack>
          </Grid>
        </Grid>
      </Box>

      {/* Applied Filters */}
      {hasActiveFilters && (
        <Stack direction='row' gap={1} flexWrap='wrap' alignItems='center'>
          {filters.providers.map((item) => (
            <Chip
              key={`provider-${String(item.value)}`}
              label={
                <Stack direction='row' gap={0.5} alignItems='center'>
                  <Typography variant='body2' sx={{ color: KLAYO_COLORS.GreyGR900 }}>
                    {translate('manage.provider')}:
                  </Typography>
                  <Typography
                    variant='body2'
                    sx={{ color: KLAYO_COLORS.GreyGR900 }}
                    fontWeight={600}
                  >
                    {item.label}
                  </Typography>
                </Stack>
              }
              onDelete={() => handleRemoveProvider(item.value)}
              deleteIcon={<RemoveIcon />}
              variant='outlined'
            />
          ))}
          {filters.regions.map((item) => (
            <Chip
              key={`region-${item.id}`}
              label={
                <Stack direction='row' gap={0.5} alignItems='center'>
                  <Typography variant='body2' sx={{ color: KLAYO_COLORS.GreyGR900 }}>
                    {translate('manage.region')}:
                  </Typography>
                  <Typography
                    variant='body2'
                    sx={{ color: KLAYO_COLORS.GreyGR900 }}
                    fontWeight={600}
                  >
                    {item.label ?? item.name}
                  </Typography>
                </Stack>
              }
              onDelete={() => handleRemoveFilterItem('regions', item.id)}
              deleteIcon={<RemoveIcon />}
              variant='outlined'
            />
          ))}
          {filters.faculties.map((item) => (
            <Chip
              key={`faculty-${item.id}`}
              label={
                <Stack direction='row' gap={0.5} alignItems='center'>
                  <Typography variant='body2' sx={{ color: KLAYO_COLORS.GreyGR900 }}>
                    {translate('manage.faculty')}:
                  </Typography>
                  <Typography
                    variant='body2'
                    sx={{ color: KLAYO_COLORS.GreyGR900 }}
                    fontWeight={600}
                  >
                    {item.label ?? item.name}
                  </Typography>
                </Stack>
              }
              onDelete={() => handleRemoveFilterItem('faculties', item.id)}
              deleteIcon={<RemoveIcon />}
              variant='outlined'
            />
          ))}
          {filters.courseTypes.map((item) => (
            <Chip
              key={`courseType-${item.id}`}
              label={
                <Stack direction='row' gap={0.5} alignItems='center'>
                  <Typography variant='body2' sx={{ color: KLAYO_COLORS.GreyGR900 }}>
                    {translate('manage.courseType')}:
                  </Typography>
                  <Typography
                    variant='body2'
                    sx={{ color: KLAYO_COLORS.GreyGR900 }}
                    fontWeight={600}
                  >
                    {item.label ?? item.name}
                  </Typography>
                </Stack>
              }
              onDelete={() => handleRemoveFilterItem('courseTypes', item.id)}
              deleteIcon={<RemoveIcon />}
              variant='outlined'
            />
          ))}
          {filters.languages.map((item) => (
            <Chip
              key={`language-${item.id}`}
              label={
                <Stack direction='row' gap={0.5} alignItems='center'>
                  <Typography variant='body2' sx={{ color: KLAYO_COLORS.GreyGR900 }}>
                    {translate('manage.language')}:
                  </Typography>
                  <Typography
                    variant='body2'
                    sx={{ color: KLAYO_COLORS.GreyGR900 }}
                    fontWeight={600}
                  >
                    {item.label ?? item.name}
                  </Typography>
                </Stack>
              }
              onDelete={() => handleRemoveFilterItem('languages', item.id)}
              deleteIcon={<RemoveIcon />}
              variant='outlined'
            />
          ))}
          {hasPriceFilter && (
            <Chip
              label={
                <Stack direction='row' gap={0.5} alignItems='center'>
                  <Typography variant='body2' sx={{ color: KLAYO_COLORS.GreyGR900 }}>
                    {translate('manage.pricingRange')}:
                  </Typography>
                  <Typography
                    variant='body2'
                    sx={{ color: KLAYO_COLORS.GreyGR900 }}
                    fontWeight={600}
                  >
                    ${filters.priceMin ?? 0} - ${filters.priceMax ?? 0}
                  </Typography>
                </Stack>
              }
              onDelete={() => handleRemoveFilter('price')}
              deleteIcon={<RemoveIcon />}
              variant='outlined'
            />
          )}
          {filters.hasEnrolments && (
            <Chip
              label={
                <Typography variant='body2' sx={{ color: KLAYO_COLORS.GreyGR900 }} fontWeight={600}>
                  {translate('manage.onlyShowCoursesWithEnrolments')}
                </Typography>
              }
              onDelete={() => handleRemoveFilter('hasEnrolments')}
              deleteIcon={<RemoveIcon />}
              variant='outlined'
            />
          )}
          <Typography
            variant='body2'
            sx={{ color: KLAYO_COLORS.NeonBlueNB500, cursor: 'pointer', ml: 1 }}
            onClick={handleClearAllFilters}
          >
            {translate('common.clearAll')}
          </Typography>
        </Stack>
      )}

      {viewMode === 'grid' ? (
        <Stack gap={5}>
          {isLoading ? (
            <CardSkeleton />
          ) : items.length === 0 ? (
            <TablePlaceholder
              text={translate('manage.noCoursesFound')}
              icon={<SearchIcon />}
              sx={{ background: 'unset' }}
            />
          ) : (
            <GridList>
              {items.map((item) => (
                <ManageTrainingCard key={item.id} item={item} />
              ))}
            </GridList>
          )}
          {totalCount > 0 && (
            <Box display='flex' justifyContent='flex-end' alignItems='center' gap={2}>
              {!isMobile && (
                <>
                  <Box display='flex' alignItems='center'>
                    <Typography variant='body2'>{translate('team.rowsPerPage')}:</Typography>
                    <Select
                      size='small'
                      value={paginationModel.pageSize.toString()}
                      sx={{ '& .MuiOutlinedInput-notchedOutline': { border: 'none' } }}
                      onChange={(event: SelectChangeEvent) => {
                        handlePaginationModelChange({
                          page: 0,
                          pageSize: Number(event.target.value),
                        });
                      }}
                      renderValue={(value) => <Typography variant='body2'>{value}</Typography>}
                    >
                      {PAGE_SIZE_OPTIONS.map((o) => (
                        <MenuItem value={o} key={o}>
                          {o}
                        </MenuItem>
                      ))}
                    </Select>
                  </Box>
                  <Typography variant='body2'>{rangeText}</Typography>
                </>
              )}
              <Pagination
                count={Math.ceil(totalCount / paginationModel.pageSize)}
                page={paginationModel.page + 1}
                onChange={(_, value) => {
                  handlePaginationModelChange({
                    page: value - 1,
                    pageSize: paginationModel.pageSize,
                  });
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                sx={{
                  '& .MuiPaginationItem-root.Mui-selected': {
                    color: KLAYO_COLORS.GreyGR050,
                    backgroundColor: KLAYO_COLORS.NeonBlueNB500,
                  },
                  display: 'flex',
                  justifyContent: 'end',
                }}
              />
            </Box>
          )}
        </Stack>
      ) : (
        <>
          <ManageTrainingTable
            rows={items}
            translate={translate}
            onCountBadgeClick={handleCountBadgeClick}
            onRecurrenceChange={handleRecurrenceChange}
            pagination={true}
            paginationMode='server'
            sortingMode='server'
            totalCount={totalCount}
            paginationModel={paginationModel}
            onPaginationModelChange={handlePaginationModelChange}
            isLoading={isLoading}
            columns={[
              { type: ManageTrainingTable.columns.courseName, field: 'title' },
              { type: ManageTrainingTable.columns.competencies, field: 'competencies' },
              { type: ManageTrainingTable.columns.duration, field: 'duration' },
              { type: ManageTrainingTable.columns.enrolled, field: 'enrolled' },
              { type: ManageTrainingTable.columns.completed, field: 'completed' },
              { type: ManageTrainingTable.columns.inProgress, field: 'inProgress' },
              { type: ManageTrainingTable.columns.overdue, field: 'overdue' },
              { type: ManageTrainingTable.columns.recurrence, field: 'recurrence' },
              { type: ManageTrainingTable.columns.actions, field: 'actions' },
            ]}
            onSortModelChange={handleSortModelChange}
            sortModel={sortModel}
            noRowsMessage={translate('manage.noCoursesFound')}
            onRowAction={handleRowAction}
            onRowSelect={handleRowSelect}
            onCompetencyClick={handleCompetencyClick}
            isRecurrenceLocked={(row) =>
              row.integrationId === CEVENTAS_PROVIDER_ID && organization?.isPaidPlan === false
            }
            onRecurrenceLocked={() => setShowPremiumDialog(true)}
            keyword={searchDebounce}
            showActions={(row) =>
              !!(isEnabledTraining && row.integrationSource === IntegrationSource.Manual)
            }
          />

          <Menu
            open={actionTarget !== null}
            anchorEl={actionTarget}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            onClose={onCloseActionMenu}
          >
            <MenuItem onClick={onOpenEditTrainingDialog}>
              <ListItemText>{translate('settingsViews.training.editTraining')}</ListItemText>
            </MenuItem>
            <MenuItem onClick={onDeleteTraining}>
              <ListItemText>{translate('settingsViews.training.deleteTraining')}</ListItemText>
            </MenuItem>
          </Menu>
        </>
      )}

      <TrainingCourseFilterDrawer
        open={showFilters}
        onClose={() => setShowFilters(false)}
        onApply={handleApplyFilters}
        filters={filters}
        providers={providerOptions}
      />

      <CourseStudentsDialog
        open={studentsDialogOpen}
        onClose={onCloseStudentsDialog}
        type={studentsDialogType}
        courseId={selectedCourse?.id}
        courseName={selectedCourse?.courseName}
        onSendReminderSuccess={(courseName) =>
          track(ManageTrainingEvent.EnrolmentsSendReminder, { courseName })
        }
      />

      {isEnabledTraining && (
        <AddTrainingDialog
          open={showAddTraining}
          mode={selectedCourse ? CREATE_TRAINING_MODE.EDIT : CREATE_TRAINING_MODE.CREATE}
          trainingData={selectedCourse}
          onClose={() => {
            setShowAddTraining(false);
            setSelectedCourse(null);
          }}
          onBeforeSave={handleBeforeSave}
          manualIntegrations={manualProviderOptions}
        />
      )}

      <CourseFinderDialog
        open={showCourseFinder}
        onClose={() => setShowCourseFinder(false)}
        isTrainingManager={true}
        onSearch={(text) => track(ManageTrainingEvent.CoursesFinderSearch, { text })}
        onResultSelected={(courseName, text) =>
          track(ManageTrainingEvent.CoursesFinderSelect, { text, courseName })
        }
      />

      {trainingAttributesDetail && (
        <TrainingAttributesDialog
          type={trainingDialogType}
          associatedAttributeDetail={trainingAttributesDetail}
          onClose={() => setTrainingAttributesDetail(null)}
          history={history}
        />
      )}

      {showDeleteConfirm && isEnabledTraining && (
        <ConfirmationDialog
          title={translate('settingsViews.training.deleteTraining')}
          question={translate('settingsViews.training.deleteTrainingConfirm')}
          cancelButton={translate('common.cancel')}
          acceptButton={translate('common.delete')}
          acceptDanger={true}
          loading={isDeleting}
          onCancel={onCancelDelete}
          onAccept={onDeleteConfirmed}
        />
      )}

      {showPremiumDialog && (
        <ConfirmationDialog
          title={translate('manage.premiumFeature')}
          question={translate('manage.premiumFeatureRecurrenceMessage')}
          isNotShowAcceptButton
          cancelButton={translate('common.close')}
          onCancel={() => setShowPremiumDialog(false)}
        />
      )}
    </Stack>
  );
};
